import { Entity, Question, Questionnaire, QuestionResponse, Section } from '../interfaces/questions.interface';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import * as uuid from 'uuid';
import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { FormComponent } from '../components/shared/questions/form/form.component';
import { MultilineTextComponent } from '../components/shared/questions/multiline-text/multiline-text.component';
import { SingleSelectionComponent } from '../components/shared/questions/single-selection/single-selection.component';
import { MultipleSelectionComponent } from '../components/shared/questions/multiple-selection/multiple-selection.component';
import { MatrixComponent } from '../components/shared/questions/matrix/matrix.component';
import { CommonQuestionnaireService } from './common-questionnaire.service';
import { QuestionTypesEnum } from '../enums/question-types.enum';
import { HttpClient } from '@angular/common/http';
import { AbstractApiV2Service } from '../../../api/services/abstract-api-v2.service';
import { ConfirmDialogService } from '../../../../common-modules/services/confirm-dialog.service';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { SingleChoiceOption, SingleChoiceQuestion } from '../interfaces/single-selection-question.interface';
import { MultipleSelectionOptions, MultipleSelectionQuestion } from '../interfaces/multiple-selection.question';
import { ToastService } from '../../../../common-modules/services/toast.service';
import { FormGroupExt } from '../../../form-ext/form-group.ext';
import { UserReadableError } from '../../../api/errors/user-readable.error';
import { MdrDashboardsService } from '../../mdr-dashboards/services/mdr-dashboards.service';
import { MatrixQuestion } from '../interfaces/matrix-question.interface';
import { BackendErrorTranslatorService } from '../../../services/localization/backend-error-translator.service';
import { Router } from '@angular/router';
import { IdentityService } from '../../auth/services/identity.service';

export interface EntityType {
    name: 'section' | QuestionTypesEnum;
}

@Injectable({
    providedIn: 'root',
})
export class CstQuestionnaireService extends AbstractApiV2Service<Questionnaire> {
    public LocalizedResourceName = $localize`questionnaire`;
    public LocalizedPluralResourceName = $localize`questionnaires`;
    public sections: Section[] = [];
    public processedEntities: Entity[] = [];
    public currentSection: Section;
    public question: Question;
    public entity: Entity;
    public createdTypeSubject = new Subject<EntityType>();
    public questionSubject = new Subject<Question>();
    public sectionsSubject = new Subject<Section[]>();
    public currentSectionSubject = new BehaviorSubject<Section>({} as Section);
    public deleteQuestionSubject = new Subject<Question>();
    public questionnaireLoaded = new BehaviorSubject<boolean>(false);
    public isUpdatingCQ: boolean;
    public isDragDropActive = false;
    public isLoading = false;
    public currentQuestionForm: FormGroupExt | null;
    public initialQuestions: Question[] = [];
    public initialSections: Section[] = [];
    public entitySubject = new BehaviorSubject<Entity>({
        entity: {
            id: uuid.v4(),
            number: this.sections.length + 1,
            section: 'Section ' + (this.sections.length + 1),
            questions: [],
            isNew: true,
            isExpanded: true,
        },
    });
    public showErrorOnForm: Subject<boolean> = new Subject<boolean>();
    public version: number;
    public defaultQuestionTypes = [
        {
            name: 'Enterprise / Premium',
            checked: true,
        },
        {
            name: 'Foundations (MSP)',
            checked: false,
        },
        {
            name: 'Foundations (SMB)',
            checked: false,
        },
    ]; // by default, every question has Enterprise and Premium enabled
    protected resourceName = 'customer-questionnaire/questionnaire';

    constructor(
        public readonly confirmDialogService: ConfirmDialogService,
        public readonly toastService: ToastService,
        public readonly mdrService: MdrDashboardsService,
        private readonly commonQuestionnaireService: CommonQuestionnaireService,
        private readonly router: Router,
        http: HttpClient,
        backendErrorTranslator: BackendErrorTranslatorService,
        identityService: IdentityService,
    ) {
        super(http, backendErrorTranslator);

        this.commonQuestionnaireService.questionnnaireIdLoadedObs().subscribe((loaded: boolean) => {
            if (loaded && this.commonQuestionnaireService.createMode && this.mdrService.isPremiumEnterprise(identityService.company)) {
                this.loadQuestionnaireById(this.commonQuestionnaireService.questionnaireId);
            }
        });
    }

    public loadQuestionnaireById(questionnaireId: string, cid?: string) {
        let resourceId = questionnaireId + (cid ? `/${cid}` : '');
        this.isLoading = true;
        this.getSingle(resourceId).subscribe(
            (questionnaire: Questionnaire) => {
                if (questionnaire.questionnaire?.length > 0) {
                    this.sections = questionnaire.questionnaire;
                    this.initialSections = [];
                    this.initialSections = _.cloneDeep(questionnaire.questionnaire);
                    this.sectionsSubject.next(this.sections);
                    this.flattenSections();
                    this.version = questionnaire.version;
                    if (!this.sections.length) {
                        const section: Section = {
                            id: uuid.v4(),
                            number: this.sections.length + 1,
                            section: '',
                            questions: [],
                            isNew: true,
                            isExpanded: true,
                        };

                        this.addSection({ entity: section });
                    } else {
                        this.initEntity({ entity: this.sections[0] });
                    }
                    this.initialQuestions = [];
                    this.sections.forEach((s) => {
                        this.initialQuestions.push(..._.cloneDeep(s.questions));
                    });
                    this.isUpdatingCQ = true;
                    this.questionnaireLoaded.next(true);
                } else {
                    this.isUpdatingCQ = false;
                }
                this.isLoading = false;
            },
            (err) => {
                this.isLoading = false;
                this.toastService.showError('Could not load questionnaire.', {
                    duration: 20000,
                });
            },
        );
    }

    public isQuestionAnsweredAfterReview(question: QuestionResponse) {
        return (
            (question.cifc_review && question.cifc_review.answered && !question.cifc_review.solved) ||
            (!question.cifc_review && question.body.updated) ||
            (question.cifc_review && question.cifc_review.solved && question.body.updated)
        );
    }

    public onDeletedQuestion(): Observable<any> {
        return this.deleteQuestionSubject.asObservable();
    }

    public getQuestionObs(): Observable<any> {
        return this.questionSubject.asObservable();
    }

    public getSections(): Observable<any> {
        return this.sectionsSubject.asObservable();
    }

    public isQuestionnaireLoaded(): Observable<boolean> {
        return this.questionnaireLoaded.asObservable();
    }

    public setEntity(entity: Entity) {
        if (this.instanceOfSection(entity?.entity)) {
            this.entity = { entity: entity?.entity as Section };
            this.currentSection = this.entity.entity as Section;
            this.currentSectionSubject.next(entity.entity as Section);
        } else {
            this.currentSection = this.getSectionByQuestionId(entity?.entity.id) || this.currentSection;
            this.entity = { entity: entity?.entity as Question };
        }
        this.entitySubject.next(entity);
    }

    public getEntityObs(): Observable<Entity> {
        return this.entitySubject.asObservable();
    }

    public setQuestion(question: Question) {
        this.questionSubject.next(question);
        this.question = question;
    }

    public getCurrentSectionObs(): Observable<Section> {
        return this.currentSectionSubject.asObservable();
    }

    public setCreatedType(type: EntityType) {
        this.createdTypeSubject.next(type);
    }

    public getCreatedTypeObs(): Observable<EntityType> {
        return this.createdTypeSubject.asObservable();
    }

    public getTotalQuestions(): number {
        let total = 0;

        this.sections.forEach((section: Section) => {
            total += section.questions.length;
        });

        return total;
    }

    public getTotalEntities(): number {
        return this.processedEntities.length;
    }

    public getSectionById(sectionId: string | undefined): Section {
        return this.sections.find((section: Section) => section.id === sectionId) ?? ({} as Section);
    }

    public initEntity(entity: Entity) {
        const section = entity?.entity as Section;

        this.entity = entity;
        this.entitySubject.next(entity);
        this.currentSection = section;
        this.currentSectionSubject.next(section);
    }

    /**
     * Function used to add an "Other" option for single or multiple questions
     * */
    public onOtherOptionChange($event: MatCheckboxChange, currentAnswer: SingleChoiceOption[] | MultipleSelectionOptions[]) {
        if ($event.checked) {
            let opt: SingleChoiceOption = {
                id: '',
                text: '',
                checked: false,
                target_question_list: [],
                is_other_option: true,
            };

            this.addQuestionField(currentAnswer, this.getObjectWithRandomId(opt));
        } else {
            const otherOption = currentAnswer.find((x: any) => x.is_other_option);
            if (otherOption) {
                currentAnswer.splice(currentAnswer.indexOf(otherOption), 1);
            }
        }
    }

    /**
     * Add a new section to the questionnaire
     * @param entity
     */
    public addSection(entity: Entity) {
        this.initEntity(entity);
        this.sections.push(entity?.entity as Section);
        this.flattenSections();
    }

    /**
     * Add a new question to the current section
     * @param entity
     * @param sectionId
     * @param type
     */
    public addQuestion(entity: Entity, sectionId: string, type: QuestionTypesEnum) {
        const question = entity?.entity as Question;
        question.id = uuid.v4();

        if (!this.question) {
            this.getSectionById(sectionId)?.questions.push(question);
        } else {
            this.getSectionById(this.currentSection.id).questions.splice(this.question.number + 1, 0, question);
        }

        this.currentSection = this.getSectionById(sectionId) ? this.getSectionById(sectionId) : this.sections[this.sections.length - 1];

        this.flattenSections();
        this.setEntity(entity);
    }

    /**
     * Duplicate question without reference
     * @param sectionId
     * @param question
     */
    public copyQuestion(sectionId: string | undefined, question: Question) {
        const questionIndex = this.getEntityIndexById(question.id);
        let copiedQuestion = _.cloneDeep(question);

        copiedQuestion.number++;
        copiedQuestion.isNew = true;
        copiedQuestion.id = uuid.v4();

        copiedQuestion.body.options?.forEach((opt: any) => {
            opt.id = uuid.v4();
        });

        this.getSectionById(sectionId)?.questions.splice(questionIndex, 0, copiedQuestion);

        this.flattenSections();
        this.setEntity({ entity: copiedQuestion });
        this.commonQuestionnaireService.onQuestionSelected(copiedQuestion as unknown as QuestionResponse, this.getSectionById(sectionId));
    }

    public buildQuestion(
        questionComponent: FormComponent | MultilineTextComponent | SingleSelectionComponent | MultipleSelectionComponent | MatrixComponent,
        newQuestion: Question,
    ) {
        this.getCurrentSectionObs().subscribe((section) => {
            let sectionIndex = section ? section.number - 1 : -1;
            questionComponent.questionPlaceholder =
                this.getQuestionNumber(newQuestion as unknown as QuestionResponse, sectionIndex) +
                '. ' +
                $localize`Type your question here`;
            questionComponent.question = newQuestion;
            questionComponent.questionBody = newQuestion?.body;
            questionComponent.questionTitle =
                this.getQuestionNumber(newQuestion as unknown as QuestionResponse, sectionIndex).toString() + '.';

            if (this.commonQuestionnaireService.createMode && questionComponent.questionBody && !questionComponent.questionBody.options) {
                if (questionComponent instanceof FormComponent) {
                    questionComponent.questionBody.options = [
                        this.getObjectWithRandomId(questionComponent.inputObject),
                        this.getObjectWithRandomId(questionComponent.selectObject),
                    ];
                }

                if (questionComponent instanceof MultipleSelectionComponent || questionComponent instanceof SingleSelectionComponent) {
                    questionComponent.questionBody.options = [
                        this.getObjectWithRandomId(questionComponent.optionObj),
                        this.getObjectWithRandomId(questionComponent.optionObj),
                    ];
                }

                if (questionComponent instanceof MultilineTextComponent) {
                    questionComponent.questionBody.options = [
                        {
                            id: uuid.v4(),
                            answer: '',
                            placeholder: '',
                        },
                    ];
                }

                if (questionComponent instanceof MatrixComponent) {
                    questionComponent.questionBody.categories = [
                        this.getObjectWithRandomId(questionComponent.categoryObj),
                        this.getObjectWithRandomId(questionComponent.categoryObj),
                    ];

                    questionComponent.questionBody.options = [
                        this.getObjectWithRandomId(questionComponent.optionObj),
                        this.getObjectWithRandomId(questionComponent.optionObj),
                    ];
                }
            }
            questionComponent.currentAnswer = newQuestion.body?.options;
        });
    }

    public moveQuestion(
        question: Question,
        fromQuestionIndex: number,
        fromSectionIndex: number,
        toQuestionIndex: number,
        toSectionIndex: number,
    ) {
        let questionToAdd = _.cloneDeep(question);
        questionToAdd.section_id = this.sections[toSectionIndex].id;
        this.sections[fromSectionIndex].questions.splice(fromQuestionIndex, 1);
        this.sections[toSectionIndex].questions.splice(toQuestionIndex, 0, questionToAdd);

        this.flattenSections();
        this.questionsRenumbering();
        this.setQuestion(this.sections[toSectionIndex].questions[toQuestionIndex]);
        this.setEntity({
            entity: this.sections[toSectionIndex].questions[toQuestionIndex],
        });
    }

    public questionsRenumbering() {
        this.sections.forEach((section: Section) => {
            section.questions.forEach((question: Question, index: number) => {
                question.number = index;
            });
        });
    }

    /**
     * Delete question from the specified entity
     * @param sectionId - delete from this section
     * @param question - question to be deleted
     */
    public deleteQuestion(sectionId: string | undefined, question: Question) {
        this.confirmDialogService.showDialog(
            {
                title: $localize`Delete`,
                description: $localize`Are you sure you want to delete this question?`,
                primaryButton: {
                    text: $localize`Delete`,
                    type: 'negative',
                    action: () => {
                        this.confirmDelete(sectionId, question);
                    },
                },
                cancelButton: {
                    text: $localize`Cancel`,
                },
            },
            {
                primaryCallback: () => {
                    this.toastService.showSuccess($localize`Question has been deleted`, {
                        duration: 5000,
                    });
                },
                errorCallback: (error: UserReadableError) => {
                    this.toastService.showError($localize`Could not delete question. ` + error.message, {
                        duration: 5000,
                    });
                },
            },
        );
    }

    public confirmDelete(sectionId: string | undefined, question: Question) {
        this.isLoading = true;
        if (question.isNew) {
            this.deleteQuestionFromFrontend(sectionId, question);
            this.isLoading = false;
        } else {
            this.deleteSingle('/question/' + question.id).subscribe(
                (result) => {
                    this.deleteQuestionFromFrontend(sectionId, question);
                    this.isLoading = false;
                },
                (err) => {
                    this.isLoading = false;
                    this.toastService.showError('Could not delete question.', {
                        duration: 2000,
                    });
                },
            );
        }
    }

    public deleteQuestionFromFrontend(sectionId: string | undefined, question: Question) {
        this.currentQuestionForm = null;
        let section = this.getSectionById(sectionId);
        let questionId = question.id;

        if (!section) {
            return;
        }

        const questionIndex = section.questions.findIndex((question: Question) => question.id === questionId);

        if (typeof questionIndex !== 'undefined') {
            section.questions.splice(questionIndex, 1);
        }

        for (let i = questionIndex; i < section?.questions.length; i++) {
            section.questions[i].number--;
        }

        this.entity = { entity: section.questions[questionIndex - 1] };

        /** update flatten section with the remaining questions */
        this.flattenSections();

        /** Set the current question to be the previous one if existed, if not set the view
         * To the current section
         * */
        if (questionIndex - 1 >= 0) {
            this.setQuestion(section.questions[questionIndex - 1]);
            this.commonQuestionnaireService.onQuestionSelected(
                section.questions[questionIndex - 1] as unknown as QuestionResponse,
                section,
            );
        } else {
            this.setEntity({ entity: section });
        }
    }

    /**
     * Method to delete the entire section (questions included)
     */
    public deleteSection() {
        if (this.currentSection.isNew) {
            const sectionIndex = this.getSectionIndexById(this.currentSection?.id);

            this.sections.splice(sectionIndex, 1);
            this.flattenSections();

            if (this.sections.length === 1) {
                this.currentSection = this.sections[0];
                this.setEntity({ entity: this.sections[0] });
            } else if (this.sections.length > 1) {
                for (let i = sectionIndex; i < this.sections.length; i++) {
                    this.sections[i].number--;
                }
                this.currentSection = this.sections[sectionIndex - 1];
                this.setEntity({ entity: this.sections[sectionIndex - 1] });
            }
        } else {
            this.isLoading = true;
            this.deleteSingle('/section/' + this.currentSection?.id).subscribe(
                (result) => {
                    this.isLoading = false;
                    this.loadQuestionnaireById(this.commonQuestionnaireService.questionnaireId);
                },
                (err) => {
                    this.isLoading = false;
                    this.toastService.showError('Could not delete section.', {
                        duration: 2000,
                    });
                },
            );
        }
    }

    /** Transform the entities object to a single-level array in order to easily get to the previous/next entity */
    public flattenSections() {
        this.processedEntities = [];

        this.sections.forEach((section: Section) => {
            this.processedEntities.push({ entity: section });
            section.questions.forEach((question: Question) => {
                this.processedEntities.push({ entity: question });
            });
        });

        if (this.processedEntities.length) {
            this.commonQuestionnaireService.questionsFilteredSubject.next(true);
        }

        this.questionsRenumbering();
    }

    /** Get current entity index */
    public currentEntityIndex(): number {
        return this.processedEntities.findIndex((entity: Entity) => entity?.entity?.id === this.entity?.entity?.id);
    }

    public getEntityIndexById(id: string | undefined): number {
        return this.processedEntities.findIndex((entity: Entity) => entity?.entity?.id === id);
    }

    public getSectionIndexById(id: string | undefined): number {
        return this.sections.findIndex((section: Section) => section.id === id);
    }

    public getSectionByQuestionId(id: string | undefined): Section | null {
        for (let i = 0; i < this.sections.length; i++) {
            for (let k = 0; k < this.sections[i].questions.length; k++) {
                if (this.sections[i].questions[k].id === id) {
                    return this.sections[i];
                }
            }
        }
        return null;
    }

    public canDeleteSection(): boolean {
        return this.sections.length > 1;
    }

    /** Check if we should show the next entity button */
    public checkNextAvailability() {
        return (
            this.currentEntityIndex() === this.getTotalEntities() - 1 || (this.currentQuestionForm && !this.currentQuestionForm.group.valid)
        );
    }

    /** Check if we should show the previous entity button */
    public checkPreviousAvailability() {
        return (
            this.processedEntities.length < 2 ||
            this.currentEntityIndex() === 0 ||
            (this.currentQuestionForm && !this.currentQuestionForm.group.valid)
        );
    }

    /**
     * Go to next entity
     * If the next entity is a section, then overwrite the current section
     */
    public nextEntity() {
        let currIndex = this.currentEntityIndex();
        const nextEntity = this.processedEntities[currIndex + 1];

        this.setEntity(nextEntity);

        if (this.instanceOfSection(nextEntity.entity)) {
            this.currentSection = this.getSectionById(nextEntity.entity.id);
            this.currentSectionSubject.next(nextEntity.entity as unknown as Section);
            this.entitySubject.next(nextEntity);
        }

        if (this.instanceOfQuestion(nextEntity.entity)) {
            this.commonQuestionnaireService.onQuestionSelected(nextEntity.entity as unknown as QuestionResponse, this.currentSection);
        }
    }

    /**
     * Go to previous entity
     * If the previous entity is a section, then overwrite the current section
     */
    public previousEntity() {
        let currIndex = this.currentEntityIndex();
        const previousEntity = this.processedEntities[currIndex - 1];
        this.currentSection = this.getSectionByQuestionId(previousEntity.entity?.id) ?? this.currentSection;
        this.setEntity(previousEntity);

        if (this.instanceOfSection(previousEntity?.entity)) {
            this.currentSection = this.getSectionById(previousEntity?.entity.id);
            this.currentSectionSubject.next(previousEntity.entity as unknown as Section);
            this.entitySubject.next(previousEntity);
        }

        if (this.instanceOfQuestion(previousEntity.entity)) {
            this.commonQuestionnaireService.onQuestionSelected(previousEntity.entity as unknown as QuestionResponse, this.currentSection);
        }
    }

    /** check if the object is a section */
    public instanceOfSection(object: any): object is Section {
        return object ? 'section' in object : false;
    }

    /** check if the object is a question */
    public instanceOfQuestion(object: any): object is Question {
        return object ? 'title' in object : false;
    }

    /**
     * Add new rows/fields to the current question
     * @param questionBody - where to add new rows
     * @param object - row to be added
     * @param customIndex - add row to the specified position
     */
    public addQuestionField(questionBody: any, object: any, customIndex?: number) {
        if (customIndex) {
            questionBody.splice(customIndex, 0, object);
        } else {
            questionBody.push(object);
        }
    }

    /**
     * Delete rows/fields from question if the minimum requirements are met
     * @param questionBody - delete rows from this question body
     * @param index - the position to delete from
     * @param min_rows - minimum requirements for the specified question
     */
    public deleteQuestionField(questionBody: any, index: number, min_rows: number) {
        if (this.checkMinRequirements(questionBody, min_rows)) {
            questionBody.splice(index, 1);
        }
    }

    public checkMinRequirements(questionBody: any, min_rows: number) {
        return questionBody.filter((x: any) => !x.is_other_option).length > min_rows;
    }

    /**
     * Generate new rows/fields for any question without keeping the reference
     * @param object
     */
    public getObjectWithRandomId(object: any): any {
        let obj = _.cloneDeep(object);
        obj.id = uuid.v4();
        return obj;
    }

    /**
     * Get current slideshow question number to display
     * @param question
     * @param sectionIndex
     */
    public getQuestionNumber(question: QuestionResponse, sectionIndex: number | undefined): number {
        if (typeof sectionIndex === 'undefined') {
            return -1;
        }

        let total = 0;

        this.sections.forEach((section: Section, index: number) => {
            if (index < sectionIndex) {
                total += section.questions.length;
            }
        });
        return total + (question as unknown as Question).number + 1;
    }

    public buildQuestionnaireObject(questionnaireId: string) {
        this.isLoading = true;
        let questionnaire = {} as Questionnaire;
        questionnaire.questionnaire = this.sections;
        this.http
            .put(this.getLocation(questionnaireId) + '/update', {
                questionnaire: questionnaire.questionnaire,
                version: this.version,
            })
            .subscribe(
                (success) => {
                    if (this.commonQuestionnaireService.createMode) {
                        this.router.navigate(['/cq/browse']);
                        this.isLoading = false;
                    }
                },
                (err) => {
                    this.isLoading = false;
                    this.toastService.showError(err.error.message, { duration: 5000 });
                    if (err.error.version_error) {
                        setTimeout(() => {
                            this.loadQuestionnaireById(questionnaireId);
                        }, 3000);
                    }
                },
            );
    }

    public getCurrentVersion(questionnaireId: string) {
        return this.http.post(this.getLocation(questionnaireId) + '/version', { version: this.version });
    }

    public getConditionalQuestions(): Section[] {
        let filteredSections: Section[] = [];

        this.sections.forEach((section: Section) => {
            let sectionObj = _.cloneDeep(section);

            sectionObj.questions = section.questions.filter((question) => question.conditional_logic);

            if (sectionObj.questions.length) {
                filteredSections.push(sectionObj);
            }
        });

        return filteredSections;
    }

    public validateQuestion(question: Question) {
        if (!question.title) {
            return true;
        }

        if (question.type_id === QuestionTypesEnum.SINGLE) {
            const hasEmptyOptions = (question.body as SingleChoiceQuestion).options.filter((x) => !x.text && !x.is_other_option).length > 0;
            return hasEmptyOptions;
        }

        if (question.type_id === QuestionTypesEnum.MULTIPLE) {
            const hasEmptyOptions =
                (question.body as MultipleSelectionQuestion).options.filter((x) => !x.text && !x.is_other_option).length > 0;
            return hasEmptyOptions;
        }

        if (question.type_id === QuestionTypesEnum.FORM) {
            const hasEmptyInputOptions = (question.body as any).options.filter((x: any) => x.type === 'input' && !x.label).length > 0;
            const hasEmptySelectOptions =
                (question.body as any).options.filter(
                    (x: any) => (x.type === 'select' && !x.label) || x.options?.filter((op: any) => !op).length > 0,
                ).length > 0;

            return hasEmptyInputOptions || hasEmptySelectOptions;
        }

        if (question.type_id === QuestionTypesEnum.MATRIX) {
            const hasEmptyOptions = (question.body as MatrixQuestion).options.filter((x: any) => !x.label).length > 0;
            const hasEmptyCategories = (question.body as MatrixQuestion).categories.filter((x: any) => !x.name).length > 0;
            return hasEmptyOptions || hasEmptyCategories;
        }

        return false;
    }

    public isQuestionInInitialQuestions(id: string | undefined) {
        return this.initialQuestions.find((x) => x.id === id);
    }

    public resetQuestionnaireDisplay() {
        this.sections.forEach((s) => {
            s.isExpanded = false;
        });
        this.setEntity({ entity: this.sections[0] });
    }
}
